import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import noImputableService from '../../services/noImputableService';

const NoImputables: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [noImputables, setNoImputables] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchNoImputables = async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const result = await noImputableService.getNoImputables({
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: search
      });
      setNoImputables(result.items || []);
      setTotalCount(result.totalCount || 0);
    } catch (error: any) {
      console.error('Error al cargar no imputables:', error);
      enqueueSnackbar('Error al cargar no imputables', { variant: 'error' });
      setNoImputables([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNoImputables();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchNoImputables(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchNoImputables(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchNoImputables(0, newRowsPerPage, searchTerm);
  };

  const handleDelete = async (nroRubro: number) => {
    if (window.confirm('¿Está seguro de eliminar este no imputable?')) {
      try {
        await noImputableService.deleteNoImputable(nroRubro);
        enqueueSnackbar('No imputable eliminado exitosamente', { variant: 'success' });
        fetchNoImputables();
      } catch (error: any) {
        console.error('Error al eliminar no imputable:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar no imputable', { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              No Imputables
            </Typography>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/contabilidad/noimputables/nuevo')}>
              Nuevo No Imputable
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {noImputables.map((noImputable) => (
                <Card key={noImputable.nroRubro} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {noImputable.nroRubro} - {(noImputable.nombreCuenta || noImputable.descripcioncta || '').trim() || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Nivel: {noImputable.segmento}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/contabilidad/noimputables/${noImputable.nroRubro}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(noImputable.nroRubro)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nivel</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {noImputables.map((noImputable) => (
                    <TableRow key={noImputable.nroRubro} hover>
                      <TableCell>{noImputable.nroRubro}</TableCell>
                      <TableCell>{(noImputable.nombreCuenta || noImputable.descripcioncta || '').trim() || '-'}</TableCell>
                      <TableCell>{noImputable.segmento}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/contabilidad/noimputables/${noImputable.nroRubro}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(noImputable.nroRubro)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default NoImputables;
